import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { AiOutlineClose, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { IoIosCloseCircle } from "react-icons/io";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SummaryApi from '../common';
import InterstedModal from '../components/InterstedModal';
import AdvancedSearchModal from '../components/AdvancedSearchModal';

// ────────────────────────────────────────────────
// THEME — same rules as AllCities.js
// primary used ONLY for header text, links, and
// primary buttons. Table action buttons use the
// standard blue (actionBlue).
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',             // normal blue for table action buttons
  actionBlueHover: '#1D4ED8',
  actionYellow: '#D97706',           // yellow-600 for View More
  actionYellowHover: '#B45309',
  actionGreen: '#10B981',            // success / download / positive
  actionGreenHover: '#059669',
  actionPurple: '#7C3AED',           // special action
  actionPurpleHover: '#6D28D9',
  actionRed: '#EF4444',              // danger / clear / deactivate
  actionRedHover: '#DC2626',
  disabledGray: '#D1D5DB',
};

// Columns that should be sticky (Edit + View More)
const STICKY_ACCESSORS = ['edit', 'view'];

// Status chip colors (semantic palette)
const STATUS_CHIP_CLASS = {
  'Most Interested': 'bg-green-100 text-green-700',
  'Interested': 'bg-yellow-100 text-yellow-700',
  'Not Interested': 'bg-red-100 text-red-700',
  'Call Back': 'bg-blue-100 text-blue-700',
};
const NEUTRAL_CHIP_CLASS = 'bg-gray-100 text-gray-600';

const InterestedStudents = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState('');
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
  // ✅ Default to "today" if URL has ?filter=today
  const [selectedRange, setSelectedRange] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('filter')?.toLowerCase() === 'today' ? 'today' : '';
  });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [formData, setFormData] = useState({
    student_name: '',
    gender: 'Male',
    father_name: '',
    mobile_number1: '',
    mobile_number2: '',
    email: '',
    aadhar_number: '',
    state: '',
    district: '',
    city: '',
    address: '',
    pincode: '',
    course: '',
    clg_name: '',
    program_type: '',
    ug_pg_course: '',
    call_back_reason: '',
    call_duration: '00:00:00.000000',
    other_reasons: '',
    is_interested: '',
    call_status: '',
    remarks: '',
    prev_call_back_reason: '',
    next_follow_up_date: '',
  });
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchCollege, setSearchCollege] = useState('');
  const [canOnlySelectHigherInterest, setCanOnlySelectHigherInterest] = useState(false);
  const tableRef = useRef(null);
  const [latestFollowUpId, setLatestFollowUpId] = useState(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  const districts = [
    'Alluri Sitharama Raju', 'Anakapalli', 'Ananthapuram', 'Annamayya', 'Bapatla', 'CHITTOOR',
    'EAST GODAVARI', 'Eluru', 'Guntur', 'Kakinada', 'Konaseema', 'Krishna', 'Kurnool',
    'Manyam', 'Nandyal', 'NTR', 'Palnadu', 'Prakasam', 'Srikakulam', 'SPS Nellore',
    'Sri Satyasai', 'Srikakulam', 'Tirupathi', 'Vizianagaram', 'Visakhapatnam',
    'West Godavari', 'YSR Kadapa'
  ];

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
      const memberType = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(memberType);

      let response;
      if (memberType === 'Admin' || memberType === 'Manager') {
        response = await fetch(SummaryApi.getAllInterestedStudents.url, {
          method: SummaryApi.getAllInterestedStudents.method,
          headers: headers,
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
        const decodedEmployeeId = atob(encodedEmployeeId);
        response = await fetch(SummaryApi.interestedStudents.url, {
          method: SummaryApi.interestedStudents.method,
          headers: headers,
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });
      }

      if (!response.ok) throw new Error('Failed to fetch data from the endpoint');
      const data = await response.json();
      const students = data?.data || [];
      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
      }));

      setStudentsData(updatedStudentsData || []);

      let latestIndex = -1;
      let latestDate = null;

      updatedStudentsData.forEach((student, index) => {
        const fud = student.follow_up_date?.trim();
        if (!fud || fud === "0000-00-00 00:00:00" || fud === "N/A") return;
        const dateObj = new Date(fud);
        if (isNaN(dateObj.getTime())) return;
        if (!latestDate || dateObj > latestDate) {
          latestDate = dateObj;
          latestIndex = index;
        }
      });

      if (latestIndex !== -1) {
        setLatestFollowUpId(updatedStudentsData[latestIndex].student_id);
      } else {
        setLatestFollowUpId(null);
      }
      setShouldAutoScroll(true);

    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userRaw = sessionStorage.getItem("user");
    const user = JSON.parse(userRaw);
    const memberType = user?.member_type;
    setMemberType(memberType);
  }, []);

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (studentsData.length > 0) {
      filterStudents(selectedFilter, searchQuery, selectedRange);
    }
  }, [studentsData, selectedFilter, searchQuery, selectedRange]);

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    filterStudents(filter, searchQuery, selectedRange);
  };

  const filterStudents = (filter, search, range) => {
    let filtered = studentsData;
    const timeZone = 'Asia/Kolkata';
    const formatToKolkata = (date) => {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    };

    if (filter !== 'All') {
      if (filter === "JEE Mains") {
        filtered = filtered.filter((student) => student.is_jee === "Jee Mains Results");
      } else {
        filtered = filtered.filter((student) => student.is_jee !== "Jee Mains Results");
      }
      if (filter === "JEE Mains2") {
        filtered = filtered.filter((student) => student.is_jee2 === "Jee Mains-2 Results");
      } else {
        filtered = filtered.filter((student) => student.is_jee2 !== "Jee Mains-2 Results");
      }
    }

    const convertToKolkataDateString = (date) => formatToKolkata(new Date(date));

    if (range === 'today') {
      const today = new Date();
      const todayFormatted = formatToKolkata(today);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === todayFormatted;
      });
    } else if (range === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = formatToKolkata(yesterday);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === yesterdayFormatted;
      });
    } else if (range === 'thisWeek') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfWeek && studentDate <= endOfWeek;
      });
    } else if (range === 'lastWeek') {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfLastWeek && studentDate <= endOfLastWeek;
      });
    } else if (range === 'custom') {
      if (!fromDate || !toDate) return;
      const fromFormatted = new Date(fromDate);
      fromFormatted.setHours(0, 0, 0, 0);
      const toFormatted = new Date(toDate);
      toFormatted.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= fromFormatted && studentDate <= toFormatted;
      });
    }

    if (['Admin', 'Manager'].includes(memberType) && search) {
      filtered = filtered.filter((student) =>
        student.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    } else if (search) {
      filtered = filtered.filter((student) =>
        student.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
  };

  useEffect(() => {
    if (studentsData?.length) {
      const dates = studentsData.map(student => new Date(student.created_date));
      const minDate = new Date(Math.min(...dates));
      setMinEnrolledDate(minDate);
      setMaxEnrolledDate(new Date());
    }
  }, [studentsData]);

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === 'custom');

    const url = new URL(window.location.href);
    if (range === 'today') {
      url.searchParams.set('filter', 'today');
    } else {
      url.searchParams.delete('filter');
    }
    window.history.replaceState({}, '', url);

    if (range !== 'custom') {
      setFromDate(null);
      setToDate(null);
      filterStudents(selectedFilter, searchQuery, range);
    } else {
      setFilteredStudents([]);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      filterStudents(selectedFilter, searchQuery, 'custom');
    }
  }, [fromDate, toDate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    filterStudents(selectedFilter, e.target.value, selectedRange);
  };

  const ugCourses = ['CSE', 'CSE(AI & ML)', 'CSE(AI & DS)', 'AERONAUTICAL ENGINERRING', 'ECE'];
  const pgCourses = [];
  const courses = formData.program_type === 'UG' ? ugCourses : formData.program_type === 'PG' ? pgCourses : [];

  const openModal = (student = null, mode = 'view') => {
    const sanitizeStudentData = (data) => {
      const sanitizedData = { ...data };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === "N/A" || sanitizedData[key] === "n/a") {
          sanitizedData[key] = "";
        }
      });
      return sanitizedData;
    };

    const reasonsForOthers = [
      "Wrong Number", "Not Reachable", "Number Not Working", "Call Declined", "Busy",
      "Switched Off", "Incoming Calls Stopped"
    ];

    const payload = {
      employee_id: student?.employee_id,
      student_name: student?.student_name,
      gender: student?.gender,
      father_name: student?.father_name,
      mobile_number1: student?.mobile_number1,
      mobile_number2: student?.mobile_number2 || '',
      email: student?.email || '',
      aadhar_number: student?.aadhar_number || '',
      state: student?.state,
      district: student?.district,
      city: student?.city,
      address: student?.address,
      pincode: student?.pincode || '',
      course: student?.course,
      clg_name: student?.clg_name,
      inter_percentage: student?.inter_percentage || '',
      program_type: student?.program_type,
      ug_pg_course: student?.ug_pg_course,
      call_status: student?.call_status || '',
      call_duration: student?.call_duration || '00:00:00.000000',
      remarks: student?.remarks || '',
      is_interested: reasonsForOthers.includes(student?.is_interested) ? "Others" : student?.is_interested,
      other_reasons: reasonsForOthers.includes(student?.is_interested) ? student?.is_interested : '',
      call_back_reason: student?.call_back_reason,
      prev_call_back_reason: student?.call_back_reason,
      is_jee: student?.is_jee,
      is_jee2: student?.is_jee2,
      next_follow_up_date: student?.next_follow_up_date ? student.next_follow_up_date.split('T')[0] : '',
    };

    const sanitizedStudent = sanitizeStudentData(payload);

    if (mode === 'edit' && sanitizedStudent.is_interested === 'Interested') {
      setCanOnlySelectHigherInterest(true);
    } else {
      setCanOnlySelectHigherInterest(false);
    }

    const [timePart = '00:00:00'] = sanitizedStudent.call_duration.split('.');
    const [hours, minutes, seconds] = timePart.split(':');

    setFormData({
      ...sanitizedStudent,
      hours: hours?.padStart(2, '0') || '00',
      minutes: minutes?.padStart(2, '0') || '00',
      seconds: seconds?.padStart(2, '0') || '00',
    });

    if (mode === 'edit') {
      setIsUpdate(true);
      setIsUpload(false);
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(course => course.trim())
        : [];
      setSelectedCourses(coursesArray);
    } else if (mode === 'add') {
      setFormData({
        student_name: '', gender: 'Male', father_name: '', mobile_number1: '', mobile_number2: '',
        email: '', aadhar_number: '', state: '', district: '', city: '', address: '', pincode: '',
        course: '', clg_name: '', remarks: '', call_status: '', call_duration: '00:00:00.000000',
        program_type: '', ug_pg_course: '', is_interested: '', inter_percentage: '', is_jee: '', is_jee2: '',
        hours: '00', minutes: '00', seconds: '00', other_reasons: '', call_back_reason: '', next_follow_up_date: '',
      });
      setIsUpdate(false);
      setIsUpload(true);
    } else {
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(course => course.trim())
        : [];
      setSelectedCourses(coursesArray);
    }

    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const handleAllClick = () => {
    setSelectedFilter('All');
    setFilteredStudents(studentsData);

    const validFollowUps = studentsData.filter(
      s =>
        s.follow_up_date &&
        s.follow_up_date !== "0000-00-00 00:00:00" &&
        s.follow_up_date !== "N/A"
    );

    if (validFollowUps.length > 0) {
      const latest = validFollowUps.reduce((prev, curr) =>
        new Date(curr.follow_up_date) > new Date(prev.follow_up_date)
          ? curr
          : prev
      );

      setLatestFollowUpId(latest.student_id);
      setTimeout(() => {
        setShouldAutoScroll(true);
      }, 100);
    } else {
      setLatestFollowUpId(null);
      setShouldAutoScroll(false);
    }

    if (gotoPage) {
      gotoPage(0);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
    setIsUpdate(false);
    setIsUpload(false);
    setSelectedCourses([]);
    setDropdownValue('');
    setCanOnlySelectHigherInterest(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'call_status' && (value === 'Not Answered' || value === '')) {
      setFormData((prev) => ({
        ...prev,
        is_interested: '',
        [name]: value,
      }));
    } else if (name === 'is_interested') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(value !== 'Call Back' && { call_back_reason: '' }),
        ...(value !== 'Others' && { other_reasons: '' }),
      }));
    } else if (name === 'program_type' && (value === 'PG' || value === '')) {
      setSelectedCourses([]);
      setDropdownValue('');
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSelectChange = (e) => {
    const selectedCourse = e.target.value;
    if (selectedCourse && !selectedCourses.includes(selectedCourse)) {
      setSelectedCourses((prev) => [...prev, selectedCourse]);
    }
    setDropdownValue('');
  };

  const handleRemoveTag = (course) => {
    setSelectedCourses((prev) => prev.filter(c => c !== course));
  };

  useEffect(() => {
    if (formData.call_duration) {
      const [timePart] = formData.call_duration.split('.');
      const [hours, minutes, seconds] = timePart.split(':').map(part => part.padStart(2, '0'));
      setFormData((prevData) => ({
        ...prevData,
        hours,
        minutes,
        seconds,
      }));
    }
  }, [formData.call_duration]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
      const decodedEmployeeId = atob(encodedEmployeeId);

      const apiEndpoint = isUpdate ? SummaryApi.updateStudent.url : SummaryApi.uploadStudent.url;
      const method = isUpdate ? SummaryApi.updateStudent.method : SummaryApi.uploadStudent.method;

      const today = new Date();
      const formattedFollowUpDate = today.toISOString();

      const payloadData = {
        employee_student_id: formData.employee_student_id,
        student_name: formData.student_name,
        last_name: '',
        gender: formData.gender,
        father_name: formData.father_name,
        mobile_number1: formData.mobile_number1,
        mobile_number2: formData.mobile_number2 || '',
        email: formData.email || '',
        aadhar_number: formData.aadhar_number || '',
        state: formData.state,
        district: formData.district,
        city: formData.city,
        address: formData.address,
        pincode: formData.pincode || '',
        course: formData.course,
        clg_name: formData.clg_name,
        inter_percentage: formData.inter_percentage || '',
        program_type: formData.program_type,
        ug_pg_course: selectedCourses.join(', '),
        call_status: formData.call_status || '',
        call_duration: formData.call_duration || '00:00:00.000000',
        remarks: formData.remarks || '',
        is_interested: formData.is_interested === 'Others' ? formData.other_reasons : formData.is_interested,
        is_jee: (formData.is_jee === "Jee Mains Results")
          ? "Jee Mains Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains Results" ? "Jee Mains Results" : "")
            : "",
        is_jee2: (formData.is_jee2 === "Jee Mains-2 Results")
          ? "Jee Mains-2 Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains-2 Results" ? "Jee Mains-2 Results" : "")
            : "",
        call_back_reason: formData.call_back_reason || '',
      };

      const payload = isUpdate
        ? {
          ...payloadData,
          student_id: selectedStudent.student_id,
          employee_id: decodedEmployeeId,
          follow_up_date: formattedFollowUpDate,
          ...(formData.next_follow_up_date && { next_follow_up_date: formData.next_follow_up_date }),
        }
        : {
          ...payloadData,
          employee_id: decodedEmployeeId,
        };

      const response = await fetch(apiEndpoint, {
        method: method,
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      if (result.status) {
        toast.success(isUpdate ? 'Student updated successfully!' : 'Student added successfully!');
        if (!isUpdate) {
          setEnrollmentCode(result.ENR_CODE);
        }
        closeModal();
        fetchStudents();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const downloadExcelReport = () => {
    const requiredFields = [
      'enrollment_code', 'student_name', 'email', 'mobile_number1', 'mobile_number2',
      'aadhar_number', 'state', 'district', 'city', 'pincode', 'course', 'clg_name',
      'created_date', 'call_status', 'call_duration', 'remarks', 'next_follow_up_date', 'employee_name'
    ];
    const filteredData = studentsData.map(student => {
      const selectedData = {};
      requiredFields.forEach(field => {
        selectedData[field] = student[field];
      });
      return selectedData;
    });

    const headersMap = {
      enrollment_code: 'Enrollment Code',
      student_name: 'Student Name',
      email: 'Email',
      mobile_number1: 'Mobile Number 1',
      mobile_number2: 'Mobile Number 2',
      aadhar_number: 'Aadhar Number',
      state: 'State',
      district: 'District',
      city: 'City',
      address: 'Address',
      pincode: 'Pincode',
      course: 'Course',
      clg_name: 'College',
      created_date: 'Enrolled Date',
      call_status: 'Call Status',
      call_duration: 'Call Duration',
      remarks: 'Remarks',
      next_follow_up_date: 'Next Follow Up Date',
      employee_name: 'Employee Name'
    };

    const formattedData = filteredData.map(student => {
      return Object.fromEntries(
        Object.entries(student).map(([key, value]) => [headersMap[key] || key, value])
      );
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, 'students_report.xlsx');
  };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "NA";
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  // ────────────────────────────────────────────────
  // Table columns — Edit / View More use actionBlue
  // ────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        Header: 'SNO',
        Cell: ({ row }) => row.index + 1,
      },
      ...(['Admin', 'Manager'].includes(memberType) ? [
        { Header: 'Employee Name', accessor: 'employee_name' },
      ] : []),
      { Header: 'Student Name', accessor: 'student_name' },
      { Header: 'District', accessor: 'district' },
      { Header: 'City', accessor: 'city' },
      { Header: 'College Name', accessor: 'clg_name' },
      {
        Header: "Address",
        accessor: "address",
        Cell: ({ value }) => {
          if (!value || value === "N/A" || value === "n/a") {
            return <span className="text-gray-400">NA</span>;
          }
          return (
            <div className="group relative max-w-[100px]">
              <span className="block truncate text-gray-700">{value}</span>
              <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 left-0 top-6 shadow-lg w-max max-w-[300px] whitespace-normal break-words pointer-events-none">
                {value}
              </div>
            </div>
          );
        },
      },
      { Header: 'Mobile Number', accessor: 'mobile_number1' },
      {
        Header: 'Call Duration (HH:MM:SS)',
        accessor: 'call_duration',
        Cell: ({ value }) => {
          if (value === "n/a") return value;
          if (value.includes(':')) {
            const [timePart] = value.split('.');
            const [hours, minutes, seconds] = timePart.split(':');
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
          }
          return value;
        },
      },
      { Header: 'Course', accessor: 'course' },
      {
        Header: 'Enrolled Date',
        accessor: 'enroll_date',
        Cell: ({ row }) => {
          const { created_date } = row.original;
          return <span>{formatDate(created_date)}</span>;
        },
      },
      {
        Header: 'Follow Up Date',
        accessor: 'follow_up_date',
        Cell: ({ row }) => {
          const { follow_up_date } = row.original;
          return <span>{formatDate(follow_up_date)}</span>;
        },
      },
      {
        Header: 'Next Follow Up Date',
        accessor: 'next_follow_up_date',
        Cell: ({ value }) => <span>{formatDate(value)}</span>,
      },
      {
        Header: 'Previous Status',
        accessor: 'prevs_is_interested',
        Cell: ({ value }) => {
          if (!value) value = 'NA';
          const chipClass = STATUS_CHIP_CLASS[value] || NEUTRAL_CHIP_CLASS;
          return (
            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>
              {value}
            </span>
          );
        },
      },
      {
        Header: 'Current Status',
        accessor: 'is_interested',
        Cell: ({ value }) => {
          const chipClass = STATUS_CHIP_CLASS[value] || NEUTRAL_CHIP_CLASS;
          return (
            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>
              {value}
            </span>
          );
        },
      },
      { Header: 'Jee Mains-1', accessor: 'is_jee' },
      { Header: 'Jee Mains-2', accessor: 'is_jee2' },
      ...(['Employee', 'Consultant', 'Team Lead'].includes(memberType) ? [{
        Header: 'Edit',
        accessor: 'edit',
        Cell: ({ row }) => (
          <button
            style={{ backgroundColor: THEME.actionBlue }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlue)}
            className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            onClick={() => openModal(row.original, 'edit')}
          >
            <AiOutlineEdit className="shrink-0" /> Edit
          </button>
        ),
      }] : []),
      {
        Header: 'View More',
        accessor: 'view',
        Cell: ({ row }) => (
          <button
            style={{ backgroundColor: THEME.actionYellow }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellowHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellow)}
            className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            onClick={() => openModal(row.original, 'view')}
          >
            <AiOutlineEye className="shrink-0" /> View More
          </button>
        ),
      },
    ],
    [memberType]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredStudents,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  // Row styling – highlight most recent follow-up
  const getRowClass = (student) => {
    if (!student) return "transition hover:bg-slate-50";

    let base = "transition";

    const fud = student.follow_up_date && student.follow_up_date !== "0000-00-00 00:00:00" && student.follow_up_date !== "N/A"
      ? new Date(student.follow_up_date)
      : null;

    const isPast = fud && !isNaN(fud.getTime()) && fud.getTime() <= new Date().getTime();
    const isLatest = student.student_id === latestFollowUpId;

    if (isLatest) {
      base += " bg-yellow-200 font-semibold";
    } else if (isPast) {
      base += " bg-green-100 font-semibold";
    } else {
      base += " hover:bg-slate-50";
    }

    return base;
  };

  // Background color for sticky cells (must match row bg to look correct)
  const getStickyCellBg = (student) => {
    if (!student) return '#FFFFFF';
    const isLatest = student.student_id === latestFollowUpId;
    const fud = student.follow_up_date && student.follow_up_date !== "0000-00-00 00:00:00" && student.follow_up_date !== "N/A"
      ? new Date(student.follow_up_date)
      : null;
    const isPast = fud && !isNaN(fud.getTime()) && fud.getTime() <= new Date().getTime();

    if (isLatest) return '#FEF08A'; // yellow-200
    if (isPast) return '#DCFCE7';    // green-100
    return '#FFFFFF';
  };

  // Auto-scroll to latest
  useEffect(() => {
    if (!shouldAutoScroll || !latestFollowUpId) return;

    const timer = setTimeout(() => {
      if (!tableRef.current) {
        console.warn("Table ref not ready yet");
        return;
      }

      const scrollAndHighlightLatest = () => {
        if (!tableRef.current) return;

        const row = tableRef.current.querySelector(`tr[data-student-id="${latestFollowUpId}"]`);
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
          setShouldAutoScroll(false);
        } else {
          setTimeout(() => {
            if (tableRef.current) {
              const retryRow = tableRef.current.querySelector(`tr[data-student-id="${latestFollowUpId}"]`);
              if (retryRow) {
                retryRow.scrollIntoView({ behavior: "smooth", block: "center" });
                setShouldAutoScroll(false);
              }
            }
          }, 300);
        }
      };

      const studentIndex = filteredStudents.findIndex(s => s.student_id === latestFollowUpId);
      if (studentIndex === -1) {
        setShouldAutoScroll(false);
        return;
      }

      const targetPage = Math.floor(studentIndex / pageSize);

      if (pageIndex !== targetPage) {
        gotoPage(targetPage);
        setTimeout(() => {
          if (tableRef.current) {
            scrollAndHighlightLatest();
          }
        }, 800);
      } else {
        scrollAndHighlightLatest();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [shouldAutoScroll, latestFollowUpId, filteredStudents, pageIndex, pageSize, gotoPage]);

  const clearFilters = () => {
    setSelectedRange('');
    setFromDate(null);
    setToDate(null);
    setIsCustomRange(false);
    setFilteredStudents(studentsData);
    setSearchQuery('');
    setSelectedFilter('All');

    const url = new URL(window.location.href);
    url.searchParams.delete('filter');
    window.history.replaceState({}, '', url);
  };

  const pageBtnClass =
    "px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

  return (
    <div className="min-h-screen w-full">
      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2"
            style={{ borderColor: THEME.primary }}
          />
        </div>
      ) : (
        <>
          <div className="w-full space-y-5">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-xl shadow-sm px-6 py-4 flex items-center gap-3">
                  <div
                    className="animate-spin rounded-full h-6 w-6 border-b-2"
                    style={{ borderColor: THEME.primary }}
                  />
                  <span className="text-sm font-semibold" style={{ color: THEME.primary }}>
                    Submitting...
                  </span>
                </div>
              </div>
            )}

            <div className='flex justify-between items-center'>
              <h1
                className="text-2xl md:text-3xl font-bold"
                style={{ color: THEME.primary }}
              >
                Interested Students
              </h1>
              <div>
                <button
                  onClick={() => {
                    setSearchModalIsOpen(true);
                    setSearchDistrict('');
                    setSearchCity('');
                    setSearchCollege('');
                  }}
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className="text-white px-4 py-2 rounded-lg ml-4 flex items-center shadow-sm transition-opacity"
                >
                  Advanced Search
                </button>
              </div>
            </div>

            <div className='flex flex-wrap justify-between items-center gap-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4'>
              <div className="inline-flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'All' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'All' ? { backgroundColor: THEME.primary } : {}}
                  onClick={handleAllClick}
                >
                  All
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'JEE Mains' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'JEE Mains' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('JEE Mains')}
                >
                  JEE Mains-1
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'JEE Mains2' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'JEE Mains2' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('JEE Mains2')}
                >
                  JEE Mains-2
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'Not JEE Mains' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'Not JEE Mains' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('Not JEE Mains')}
                >
                  Non-JEE Mains
                </button>
              </div>

              {['Admin', 'Manager'].includes(memberType) && (
                <div>
                  <div className="flex items-center">
                    <label htmlFor="dateRange" style={{ color: THEME.primary }} className="text-sm font-semibold">
                      Select Date Range:
                    </label>
                    <select
                      id="dateRange"
                      style={{ color: THEME.primary }}
                      className='py-2 px-4 ml-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]'
                      onChange={handleRangeChange}
                      value={selectedRange}
                    >
                      <option value="">Select a range</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="thisWeek">This Week</option>
                      <option value="lastWeek">Last Week</option>
                      <option value="custom">Custom</option>
                    </select>
                    <button
                      onClick={clearFilters}
                      style={{ backgroundColor: THEME.actionRed }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                      className="px-4 py-2 ml-4 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors focus:outline-none"
                    >
                      Clear Filters
                    </button>
                  </div>

                  {isCustomRange && (
                    <div className='flex mt-4 gap-4'>
                      <div>
                        <label className="text-sm font-semibold" style={{ color: THEME.primary }}>From:</label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={minEnrolledDate}
                          maxDate={toDate || maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select From Date"
                          className='p-2 ml-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]'
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold" style={{ color: THEME.primary }}>To:</label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate || minEnrolledDate}
                          maxDate={maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select To Date"
                          className='p-2 ml-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] disabled:bg-gray-100'
                          disabled={!fromDate}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='w-full md:w-1/3'>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={['Admin', 'Manager'].includes(memberType)
                    ? "Search by student name or mobile number or employee name"
                    : "Search by student name or mobile number"}
                  className="py-2 px-4 border border-gray-300 rounded-lg w-full text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div ref={tableRef} className="overflow-x-auto">
                <table {...getTableProps()} className="min-w-full mb-4 rounded-lg overflow-hidden text-sm text-gray-700">
                  <thead className="bg-gray-200">
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => {
                          const isSticky = STICKY_ACCESSORS.includes(column.id) ||
                            STICKY_ACCESSORS.includes(column.accessor);
                          return (
                            <th
                              {...column.getHeaderProps()}
                              className={`px-4 py-2 text-left font-semibold whitespace-nowrap ${isSticky ? 'sticky right-0 z-20 bg-gray-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]' : ''}`}
                              style={{ color: THEME.primary }}
                            >
                              {column.render('Header')}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page?.length <= 0 ? <div className='text center'>No Data</div> :
                      <>
                        {page.map(row => {
                          prepareRow(row);
                          const student = row.original;
                          const studentId = student.student_id;
                          const rowClass = getRowClass(student);
                          const stickyBg = getStickyCellBg(student);

                          const stickyColsInOrder = row.cells.filter(cell =>
                            STICKY_ACCESSORS.includes(cell.column.id) ||
                            STICKY_ACCESSORS.includes(cell.column.accessor)
                          );

                          return (
                            <tr
                              {...row.getRowProps()}
                              data-student-id={studentId}
                              className={rowClass}
                            >
                              {row.cells.map(cell => {
                                const isSticky =
                                  STICKY_ACCESSORS.includes(cell.column.id) ||
                                  STICKY_ACCESSORS.includes(cell.column.accessor);

                                if (!isSticky) {
                                  return (
                                    <td {...cell.getCellProps()} className="border border-slate-100 px-4 py-2">
                                      {cell.render('Cell')}
                                    </td>
                                  );
                                }

                                const stickyIndexInRow = stickyColsInOrder.indexOf(cell);
                                const totalSticky = stickyColsInOrder.length;
                                const stickyWidths = stickyColsInOrder.map(c =>
                                  (c.column.accessor === 'edit' || c.column.id === 'edit') ? 120 : 150
                                );
                                let rightOffset = 0;
                                for (let i = stickyIndexInRow + 1; i < totalSticky; i++) {
                                  rightOffset += stickyWidths[i];
                                }

                                return (
                                  <td
                                    {...cell.getCellProps()}
                                    className="border border-slate-100 px-4 py-2 sticky z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]"
                                    style={{
                                      right: `${rightOffset}px`,
                                      backgroundColor: stickyBg,
                                      minWidth: `${stickyWidths[stickyIndexInRow]}px`,
                                      width: `${stickyWidths[stickyIndexInRow]}px`,
                                    }}
                                  >
                                    {cell.render('Cell')}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </>
                    }
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center">
                <span style={{ color: THEME.primary }} className="text-sm font-medium">
                  Total records:{' '}
                  <span className="font-semibold">{filteredStudents.length}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    style={canPreviousPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {"<<"}
                  </button>
                  <button
                    onClick={previousPage}
                    disabled={!canPreviousPage}
                    style={canPreviousPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {"<"}
                  </button>
                  <span
                    className="px-3 py-1 mx-1 rounded-lg text-white text-sm font-semibold"
                    style={{ backgroundColor: THEME.primary }}
                  >
                    Page {pageIndex + 1} of {pageCount}
                  </span>
                  <button
                    onClick={nextPage}
                    disabled={!canNextPage}
                    style={canNextPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {">"}
                  </button>
                  <button
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                    style={canNextPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {">>"}
                  </button>
                </div>
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  style={{ color: THEME.primary }}
                  className="ml-4 px-2 py-1 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                >
                  {[5, 10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <InterstedModal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              selectedStudent={selectedStudent}
              isUpdate={isUpdate}
              isUpload={isUpload}
              formData={formData}
              setFormData={setFormData}
              selectedCourses={selectedCourses}
              setSelectedCourses={setSelectedCourses}
              dropdownValue={dropdownValue}
              setDropdownValue={setDropdownValue}
              canOnlySelectHigherInterest={canOnlySelectHigherInterest}
              loadingSubmit={loadingSubmit}
              handleChange={handleChange}
              handleSelectChange={handleSelectChange}
              handleRemoveTag={handleRemoveTag}
              handleSubmit={handleSubmit}
              memberType={memberType}
              courses={courses}
              districts={districts}
            />

            <AdvancedSearchModal
              isOpen={searchModalIsOpen}
              onRequestClose={() => {
                setSearchModalIsOpen(false);
                setSearchDistrict('');
                setSearchCity('');
                setSearchCollege('');
              }}
              filteredStudents={filteredStudents}
              searchDistrict={searchDistrict}
              setSearchDistrict={setSearchDistrict}
              searchCity={searchCity}
              setSearchCity={setSearchCity}
              searchCollege={searchCollege}
              setSearchCollege={setSearchCollege}
              setFilteredStudents={setFilteredStudents}
              studentsData={studentsData}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default InterestedStudents;